/**
 * Created by fers on 26.09.15.
 */

import { declareLinkedEntity } from '../fw/MetaDataHelper.ts';
import { v4 as uuidv4 } from 'uuid';
import * as Permission from '../util/Permission';
import {channelTags} from './ChannelDefType';
import { DEFAULT_CURRENCY } from '../locales';

export const RuleTypes = {
    "integrated": {
        name: "Integrated data",
        order: 1,
    },
    "destination": {
        name: "Destination page",
        order: 2,
    },
    "helping": {
        name: "Helping tool",
        order: 3,
    },
};

export function sortRules(a, b) {
	if (a.order && b.order && a.order !== b.order) {
		return a.order - b.order;
	}

    let oa = RuleTypes[a['ruleType']] ? RuleTypes[a['ruleType']]['order'] : 4;
    let ob = RuleTypes[b['ruleType']] ? RuleTypes[b['ruleType']]['order'] : 4;

	if (oa !== ob) {
		return oa > ob;
	}

	return a['key'] > b['key'];
}

export default declareLinkedEntity({
    entity: 'scenarioChannelDefRules',
    graphQL: "ScenarioChannelDefRule",
    name: "Application Definition Rules for Scenario",
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    parentRelation: "channelDefRules",
    defaultOrder: ['key', 'include'],
    defaultTitle: (data) => {
        if (data['name']) {
            return data['name'];
        } else if (data['key']) {
            return data['key'];
        } else if (data['ruleType']) {
            return RuleTypes[data['ruleType']].name;
        }
    },
    isPublic: true,
    dynamoPrefix: 'def',
    storageType: 'schema',
    onModify: ['scenarioDef'],
    fields: (types) => {
        return {
            id: {
                name: "Rule ID",
                type: "id",
                primaryKey: true,
                required: true,
                compositeKey: true,
                userEditable: false,
                defaultValue: () => {
                    return uuidv4();
                },
                isPublic: true,
            },
            name: {
                name: "Name",
                type: "string",
                translatable: true,
                description: "Name of {key} that will be used in generic description Scenario Type.",
                defaultValue: "",
                isPublic: true,
            },
            key: {
                name: "Key",
                type: "string",
                defaultValue: "",
                description: "Variable name that will be used for replacing fragments.",
                required: true,
                validations: {
                    matchRegexp: /^[a-zA-Z]+[a-zA-Z0-9_]*$/,
                },
                isPublic: true,
            },
            include: {
                name: "Include",
                type:  "enum",
                defaultValue: "include",
                description: "Allows to exclude some Applications (order is Include, Exclude).",
                values: {
                    "include": {
                        name: "Include",
                    },
                    "exclude": {
                        name: "Exclude",
                    },
                },
                isPublic: true,
            },
            scenarioDescription: {
                name: "Scenario description",
                type: "text",
                translatable: true,
                isPublic: true,
            },
            replaceScenarioDescription: {
                name: "Replace Scenario description",
                type: "enum",
                defaultValue: "append",
                values: {
                    "append": {
                        name: "Append at end",
                    },
                    "replace": {
                        name: "Replace description",
                    },
                },
                isPublic: true,
            },
            tags: {
                name: "Tags",
                type: "enums",
                values: channelTags,
                description: "Limit this rule to only Applications with this tags.",
            },
            allTags: {
                name: "Tag requirement",
                type: "enum",
                defaultValue: "or",
                values: {
                    "or": {
                        name: "Any (or)",
                    },
                    "and": {
                        name: "All (and)",
                    },
                },
            },
            scenarioDef: {
                name: "Scenario Definition",
                to: () => types['scenarioDefs'],
                type: "relation",
                required: true,
                parent: true,
                compositeKey: true,
                hiddenCreate: true,
                defaultValue: (scenarioDef) => {
                    return scenarioDef['id'];
                },
            },
            showContactUs: {
                name: "Show Contact Us",
                type: "boolean",
                defaultValue: false,
                isPublic: true,
            },
            ruleType: {
                name: "Rule Type",
                type:  "enum",
                defaultValue: "integrated",
                values: RuleTypes,
                isPublic: true,
            },
            requiresCaching: {
                name: "Requires activated caching",
                type: "boolean",
                defaultValue: false,
                isPublic: true,
            },
            requiresWritePermission: {
                name: "Requires write permission for API",
                type: "boolean",
                defaultValue: false,
                isPublic: true,
            },
            doNotShowToUser: {
                name: "Do not show to user",
                type: "boolean",
                defaultValue: false,
                isPublic: true,
            },
            overridePrices: {
                name: "Override flow prices (deprecated)",
                type: "boolean",
                defaultValue: false,
            },
            overridePricePerInvocation: {
                name: "Price per invocation",
                type: "number",
                visibility: (data) => { return data['overridePrices']; },
                addonAfter: DEFAULT_CURRENCY,
            },
            overridePriceInstallation: {
                name: "Installation Price",
                type: "number",
                visibility: (data) => { return data['overridePrices']; },
                addonAfter: DEFAULT_CURRENCY,
            },
            overridePriceInstallationNext: {
                name: "Installation Price (another)",
                type: "number",
                visibility: (data) => { return data['overridePrices']; },
                addonAfter: DEFAULT_CURRENCY,
            },
            overridePriceSelfInstallation: {
                name: "Self Installation Price",
                type: "number",
                visibility: (data) => { return data['overridePrices']; },
                addonAfter: DEFAULT_CURRENCY,
            },
            overrideTablePerInvocation: {
                name: "Table per invocation",
                type: "string",
                visibility: (data) => { return data['overridePrices']; },
                addonAfter: DEFAULT_CURRENCY,
            },
            overridePricePerInstance: {
                name: "Price per instance",
                type: "number",
                visibility: (data) => { return data['overridePrices']; },
                addonAfter: DEFAULT_CURRENCY,
            },
            overridePricePerInstanceNext: {
                name: "Price per instance (more)",
                type: "number",
                visibility: (data) => { return data['overridePrices']; },
                addonAfter: DEFAULT_CURRENCY,
            },
            overrideMinPrice: {
                name: "Minimum Price",
                type: "number",
                visibility: (data) => { return data['overridePrices']; },
                addonAfter: DEFAULT_CURRENCY,
            },
            overridePriceText: {
                name: "Price text",
                type: "string",
                translatable: true,
            },
            entityDef: {
                name: "Entity Definition",
                to: () => types['entityDefs'],
                description: "Limit this rule to only Applications that map this Entity Definition.",
                type: "relation",
            },
            mappedFieldDef: {
                name: "Mapped Field Definition",
                description: "Match this rule only when this Field Definition is mapped.",
                to: () => types['fieldDefs'],
                type: "relation",
                visibility: (data) => { return data['entityDef'] || data['entityDef_id'] ? true : false; },
                relativeTo: ['mappedFieldEntityDef'],
            },
            mappedFieldEntityDef: {
                name: "Mapped Entity Definition",
                to: () => types['entityDefs'],
                type: "relation",
                visibility: (data) => { return data['entityDef'] || data['entityDef_id'] ? true : false; },
            },
            channelDef: {
                name: "Application Definition",
                description: "Specify Application as part of this rule.",
                to: () => types['channelDefs'],
                type: "relation",
            },
			order: {
				name: "Order",
				type: "int",
				defaultValue: 0,
				required: false,
				isPublic: true,
			},
        };
    }
});
